// @flow
import _ from 'lodash'
import React, { Component } from 'react'
import ImagePreview from '../ImagePreview'
import getDefaultCrop from '../getDefaultCrop'
import AdEditContainer from '../AdEditContainer'
import Checkbox from '../controls/Checkbox'
import { translations, translateFunc } from '../../shared/localize'
import ProviderStatuses from '../AdvertisementsTable/ProviderStatuses'
import styled, { type StyledComponent } from 'styled-components'
import type {
  AdvertisementStatistics,
  Configs,
  AudienceData,
  ValidatedAdvertisementWithStoredMedia,
  PersistedMedia
} from '../types'
import Modal from 'react-responsive-modal'

function clearFix() {
  return `
    &:after {
      display: table;
      content: '';
      clear: both;
    }
  `
}

const Title: StyledComponent<Object, any, any> = styled.div`
  font-weight: bold;
  font-size: 14px;
  padding-bottom: 3px;
`

const Description: StyledComponent<Object, any, any> = styled.div`
  font-size: 11px;
  padding-bottom: 5px;
`

const Edit: StyledComponent<Object, any, any> = styled.div`
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
`

const Wrapper: StyledComponent<Object, any, any> = styled.div`
  ${clearFix()} padding-top: 10px;
  padding-bottom: 10px;
  border-top: solid 1px #ccc;

  ${Title}, ${Description}, ${Edit} {
    ${({ status }: Object) => {
      let color = null
      if (status === 'disabled') color = 'gray'
      if (status === 'errors') color = 'red'
      if (!color) return ''
      return `color: ${color}; a { color: ${color}; }`
    }}
  }
  &:last-of-type {
    border-bottom: solid 1px #ccc;
  }
`

const CheckBoxWrapper: StyledComponent<Object, any, any> = styled.div`
  margin-top: 10px;
  float: left;
`

const TextData: StyledComponent<Object, any, any> = styled.div`
  float: left;
  padding-left: 10px;
`

const ImageBlock: StyledComponent<Object, any, any> = styled.div`
  padding-left: 5px;
  float: left;
`

const StatData: StyledComponent<Object, any, any> = styled.div`
  float: right;
  display: table;
`

const StatDataItem: StyledComponent<Object, any, any> = styled.div`
  vertical-align: middle;
  display: table-cell;
`

const Basic = styled(StatDataItem)`
  font-size: 14px;
  line-height: 16px;
  padding-right: 12px;
`

const Extra = styled(StatDataItem)`
  font-size: 12px;
  padding-right: 12px;
`

const StatusesWrapper = styled(StatDataItem)`
  min-height: 50px;
  min-width: 150px;
`

const Metric = styled.div`
  ${clearFix()} width: 120px;
  padding-bottom: 1px;
`

const MetricTitle = styled.div`
  float: left;
`

const Value = styled.div`
  float: right;
`
const Overflow = styled.div`
  width: 100%;
  overflow-x: auto;
`

export type Props = {|
  action: 'copy' | 'update' | 'create',
  dispatch: any,
  advertisement: ValidatedAdvertisementWithStoredMedia,
  audienceType: string,
  providers: Array<string>,
  isDisabled: boolean,
  checked?: boolean,
  onToggle?: Function,
  statistics?: AdvertisementStatistics,
  image: ?PersistedMedia,
  storeId: string,
  disabled?: boolean,
  configs: Configs,
  audienceData: AudienceData,
  dataStatus: 'disabled' | 'errors' | 'correct'
|}

type State = {|
  isOpen: boolean,
  modified: boolean
|}

export default class AdPreview extends Component<Props, State> {
  t: Function

  constructor(props: Props) {
    super(props)

    this.t = translateFunc(translations.ads_list)
    this.state = {
      isOpen: false,
      modified: false
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (
      this.props.audienceData.placementType !== nextProps.audienceData.placementType ||
      !_.isEqual(this.props.providers, nextProps.providers)
    ) {
      this.setState({ modified: true })
    }
  }

  render() {
    const {
      onToggle,
      checked,
      storeId,
      disabled,
      configs,
      audienceData,
      advertisement,
      image
    } = this.props
    const status = this.props.dataStatus
    const { title, columnDescription, multiDescription } = advertisement

    return (
      <Wrapper status={status}>
        <CheckBoxWrapper>
          <Checkbox
            disabled={disabled}
            checked={!!checked}
            onChange={(checked) => onToggle && onToggle(checked)}
          />
        </CheckBoxWrapper>
        <ImageBlock>
          {image && (
            <ImagePreview
              imageSrc={image.src}
              size={{ width: image.width, height: image.height }}
              width={50}
              crop={getDefaultCrop(image.width, image.height, 1.0)}
            />
          )}
        </ImageBlock>
        <TextData>
          <Title>{title}</Title>
          <Description>{columnDescription || multiDescription}</Description>
          <Edit onClick={() => this.toggle()}>
            {this.state.isOpen
              ? this.t('minimize')
              : '✎ ' + (status === 'errors' ? this.repairComment(advertisement) : this.t('edit'))}
          </Edit>
        </TextData>
        {this.renderStatistics(advertisement)}
        <Modal
          styles={{ modal: { width: '100%', maxWidth: '820px' } }}
          open={this.state.isOpen}
          onClose={() => this.setState({ isOpen: false })}
        >
          <Overflow>
            <AdEditContainer
              configs={configs}
              action={this.props.action}
              audienceData={audienceData}
              compact
              storeId={storeId}
              onChange={() => {}}
              maxMediaCount={1}
              fbPromotionPosts={[]}
            />
          </Overflow>
        </Modal>
      </Wrapper>
    )
  }

  renderStatistics(advertisement: Object) {
    const { statistics } = this.props

    if (!statistics) return null

    return (
      <StatData>
        <Basic>
          {this.renderMetrics({
            impressions: statistics.impressions_string,
            clicks: statistics.clicks_string,
            ctr: statistics.ctr_string
          })}
        </Basic>
        <Extra>
          {this.renderMetrics({
            spent: statistics.spent_string,
            cpc: statistics.cpc_string,
            goal_price: statistics.goal_price_string,
            goal_percentage: statistics.goal_percentage_string
          })}
        </Extra>
        <StatusesWrapper>
          <ProviderStatuses {...advertisement.provider_statuses} hidePlayPauseButtons />
        </StatusesWrapper>
      </StatData>
    )
  }

  renderMetrics(pairs: Object): Array<Function> {
    return _.map(pairs, (value, key: string) => (
      <Metric key={key}>
        <MetricTitle>{this.t(key)}:</MetricTitle>
        <Value>{value}</Value>
      </Metric>
    ))
  }

  repairComment(advertisement: Object) {
    if (!_.isEmpty(advertisement.imagesErrors) || !_.isEmpty(advertisement.smallImagesErrors)) {
      return this.t('load_new_image')
    } else {
      return this.t('fix')
    }
  }

  toggle() {
    this.setState((state) => ({ isOpen: !state.isOpen }))
  }
}
